import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import { renderTextWithBreaks } from "@/utils/stripHTML";
import { cn } from "@/utils/cn";
import { motion, AnimatePresence } from "framer-motion";
import { useFullPageContext } from "@/context/FullPageContext";

export interface StatItem {
  id?: string;
  number: string;
  label: string;
}

export interface ConnectionCard {
  id: string;
  title: string;
  description: string;
  subtitle?: string;
  subdescription?: string;
  stats?: StatItem[];
  picture?: string;
  image?: string;
  active?: boolean;
}

export interface StoryGridData {
  tag: string;
  title: string;
  description: string;
  cards: ConnectionCard[];
}

interface StoryGridSectionProps {
  data: StoryGridData;
  onCardSelect?: (cardId: string) => void;
}

const LIGHT_SPECKS = [
  {
    top: "12%",
    left: "6%",
    width: "6px",
    height: "6px",
    radius: "rounded-full",
    delay: "0s",
    duration: "6s",
  },
  {
    top: "18%",
    left: "18%",
    width: "11px",
    height: "7px",
    radius: "rounded-[40%_60%_70%_30%]",
    delay: "1.8s",
    duration: "7.5s",
  },
  {
    top: "25%",
    left: "34%",
    width: "5px",
    height: "8px",
    radius: "rounded-[60%_40%_50%_50%]",
    delay: "3.2s",
    duration: "8s",
  },
  {
    top: "32%",
    left: "10%",
    width: "8px",
    height: "8px",
    radius: "rounded-full",
    delay: "0.4s",
    duration: "5.5s",
  },
  {
    top: "38%",
    left: "28%",
    width: "10px",
    height: "6px",
    radius: "rounded-[50%_50%_30%_70%]",
    delay: "2.1s",
    duration: "6.8s",
  },

  {
    top: "45%",
    left: "8%",
    width: "7px",
    height: "7px",
    radius: "rounded-full",
    delay: "0.9s",
    duration: "6.5s",
  },
  {
    top: "52%",
    left: "22%",
    width: "13px",
    height: "7px",
    radius: "rounded-[30%_70%_60%_40%]",
    delay: "2.4s",
    duration: "9s",
  },
  {
    top: "58%",
    left: "38%",
    width: "6px",
    height: "6px",
    radius: "rounded-full",
    delay: "1.3s",
    duration: "7s",
  },

  {
    top: "66%",
    left: "5%",
    width: "9px",
    height: "9px",
    radius: "rounded-full",
    delay: "1.6s",
    duration: "6.2s",
  },
  {
    top: "72%",
    left: "16%",
    width: "6px",
    height: "6px",
    radius: "rounded-full",
    delay: "4.1s",
    duration: "7.2s",
  },
  {
    top: "62%",
    left: "30%",
    width: "12px",
    height: "7px",
    radius: "rounded-[60%_40%_70%_30%]",
    delay: "0.7s",
    duration: "7.8s",
  },

  {
    top: "78%",
    left: "12%",
    width: "10px",
    height: "6px",
    radius: "rounded-[50%_50%_30%_70%]",
    delay: "1.2s",
    duration: "8.5s",
  },
  {
    top: "84%",
    left: "25%",
    width: "6px",
    height: "6px",
    radius: "rounded-full",
    delay: "3.6s",
    duration: "7.4s",
  },
  {
    top: "88%",
    left: "14%",
    width: "8px",
    height: "8px",
    radius: "rounded-full",
    delay: "4.5s",
    duration: "6.3s",
  },
];

export default function StoryGridSection({
  data,
  onCardSelect,
}: StoryGridSectionProps) {
  const cards = data?.cards && data.cards.length > 0 ? data.cards : [];
  const { activeIndex } = useFullPageContext();
  const sectionRef = useRef<HTMLElement>(null);

  // Default: NO card active initially
  const [activeCardId, setActiveCardId] = useState<string | null>(null);

  // Auto reset activeCardId = null whenever user scrolls to/away (activeIndex changes)
  useEffect(() => {
    setActiveCardId(null);
  }, [activeIndex]);

  // IntersectionObserver to reset activeCardId = null on scroll in/out
  useEffect(() => {
    const observer = new IntersectionObserver(
      () => {
        setActiveCardId(null);
      },
      { threshold: 0.15 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  if (!data) return null;

  const handleCardClick = (id: string) => {
    setActiveCardId(id);
    if (onCardSelect) {
      onCardSelect(id);
    }
  };

  const tag = data?.tag || "BỐN LỚP KẾT NỐI";
  const title = data?.title || "Một tâm điểm.\nBốn chiều giá\ntrị.";
  const description =
    data?.description && data.description.trim() !== ""
      ? data.description
      : "Chạm vào từng lớp để khám phá câu chuyện phía sau Dragon Home.";

  const activeIdx = activeCardId
    ? cards.findIndex((c) => c.id === activeCardId)
    : -1;
  const isLeftColumnActive = activeIdx === 0 || activeIdx === 2;
  const isTopRowActive = activeIdx === 0 || activeIdx === 1;

  const gridColsStyle =
    activeIdx < 0 ? "1fr 1fr" : isLeftColumnActive ? "70% 1fr" : "1fr 70%";
  const gridRowsStyle =
    activeIdx < 0 ? "1fr 1fr" : isTopRowActive ? "70% 1fr" : "1fr 70%";

  return (
    <section
      ref={sectionRef}
      className="relative w-full h-auto lg:h-screen lg:min-h-screen bg-primaryDark text-white flex items-center justify-center px-4 sm:px-8 md:px-12 lg:px-16 py-12 sm:py-16 lg:py-16 overflow-hidden select-none"
    >
      <style>{`
        @keyframes sunlightTwinkle {
          0%, 100% {
            opacity: 0.05;
            transform: scale(0.85) translate(0, 0);
          }
          50% {
            opacity: 0.35;
            transform: scale(1.15) translate(-2px, 2px);
          }
        }
      `}</style>

      {/* BACKGROUND GRADIENT IN TAILWIND PRIMARY & PRIMARY DARK PALETTE */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-[#085C47] to-primaryDark pointer-events-none z-0 opacity-95" />
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/25 rounded-full blur-3xl pointer-events-none" />

      {/* DECORATIVE SHADOW OVERLAY IMAGE */}
      <div className="absolute inset-y-0 left-0 w-full lg:w-[55%] h-full pointer-events-none z-[1] opacity-100 mix-blend-overlay overflow-hidden">
        <Image
          src="/images/deco_shadow_left_bottom.png"
          alt="Decorative Shadow Overlay"
          fill
          sizes="(max-width: 1024px) 100vw, 55vw"
          priority
          className="object-cover object-left-bottom"
        />
      </div>

      {/* DAPPLED SUNLIGHT SPECKS */}
      <div className="absolute inset-y-0 left-0 w-full lg:w-[48%] h-full pointer-events-none z-[2] overflow-hidden">
        {LIGHT_SPECKS.map((speck, idx) => (
          <div
            key={idx}
            className={`absolute ${speck.radius} bg-[#FFF8E7] blur-[2px] shadow-[0_0_8px_rgba(255,248,231,0.5)]`}
            style={{
              top: speck.top,
              left: speck.left,
              width: speck.width,
              height: speck.height,
              animation: `sunlightTwinkle ${speck.duration} infinite ease-in-out ${speck.delay}`,
            }}
          />
        ))}
      </div>

      {/* MAIN CONTAINER GRID: 4 COLUMNS LEFT, 8 COLUMNS RIGHT */}
      <div className="relative z-10 max-w-7xl w-full grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-8 items-center">
        {/* LEFT COLUMN: TITLE & DESCRIPTION (4 COLUMNS) */}
        <div className="lg:col-span-4 space-y-4 sm:space-y-6 md:space-y-8">
          <div className="flex items-center gap-3">
            <span className="w-8 h-[1px] bg-white/40" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60 font-sans">
              {tag}
            </span>
          </div>

          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-white tracking-tight title-leading"
            style={{ lineHeight: 1.15 }}
          >
            {renderTextWithBreaks(title)}
          </h2>

          <p className="text-white/80 text-sm md:text-base font-light font-sans max-w-md leading-relaxed">
            {renderTextWithBreaks(description)}
          </p>
        </div>

        {/* RIGHT COLUMN: DYNAMIC 2x2 EXPANDING GRID (8 COLUMNS) */}
        <div className="lg:col-span-8 w-full">
          <div
            className="w-full h-auto min-h-[480px] sm:min-h-0 sm:h-[540px] md:h-[560px] lg:h-[580px] relative rounded-sm overflow-hidden"
            style={{
              display: "grid",
              gridTemplateColumns: gridColsStyle,
              gridTemplateRows: gridRowsStyle,
              gap: "0.85rem",
              transition:
                "grid-template-columns 0.75s cubic-bezier(0.25, 1, 0.35, 1), grid-template-rows 0.75s cubic-bezier(0.25, 1, 0.35, 1)",
            }}
          >
            {cards.map((card) => {
              const isSelected = activeCardId === card.id;

              return (
                <motion.div
                  key={card.id}
                  layout
                  transition={{ duration: 0.75, ease: [0.25, 1, 0.35, 1] }}
                  onClick={() => handleCardClick(card.id)}
                  className={cn(
                    "p-4 sm:p-5 md:p-6 rounded-none cursor-pointer flex flex-col justify-between border relative overflow-hidden transition-colors duration-300 w-full h-full",
                    isSelected
                      ? "bg-secondary-light text-primaryDark border-secondary-light shadow-2xl scale-[1.005] z-10"
                      : "bg-primaryDark/80 hover:bg-primary text-white border-white/20 hover:border-secondary-light/50 backdrop-blur-sm z-0",
                  )}
                >
                  {/* TOP ROW: ID & ARROW */}
                  <div className="flex items-center justify-between w-full flex-shrink-0">
                    <span
                      className={cn(
                        "text-xs md:text-sm font-mono tracking-widest transition-colors duration-300",
                        isSelected
                          ? "text-primaryDark/70 font-bold"
                          : "text-white/50",
                      )}
                    >
                      {card.id}
                    </span>
                    <Image
                      src="/icons/arrow_outward.svg"
                      alt="arrow"
                      width={18}
                      height={18}
                      className={cn(
                        "w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-300 object-contain",
                        isSelected
                          ? ""
                          : "invert brightness-200 opacity-60 group-hover:opacity-100",
                      )}
                    />
                  </div>

                  {/* CONTENT SWITCH */}
                  {!isSelected ? (
                    /* INACTIVE SHRUNKEN CARD CONTENT */
                    <div className="space-y-1.5 mt-auto pt-2 sm:pt-3 overflow-hidden">
                      <h3 className="text-base sm:text-xl md:text-2xl font-lora font-normal leading-tight line-clamp-none sm:line-clamp-2 text-white pt-1">
                        {card.title}
                      </h3>
                      <p className="text-xs sm:text-sm font-light font-sans text-white/70 line-clamp-none sm:line-clamp-1 pt-0.5">
                        {card.description}
                      </p>
                    </div>
                  ) : (
                    /* ACTIVE EXPANDED CARD CONTENT (FULL WIDTH TEXT - IMAGES TEMPORARILY HIDDEN) */
                    <motion.div
                      key="active"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{
                        duration: 0.35,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                      className="w-full h-[calc(100%-1.75rem)] mt-2 overflow-hidden flex flex-col justify-between"
                    >
                      <div className="w-full h-full flex flex-col justify-between overflow-hidden">
                        <div className="space-y-1.5 overflow-hidden">
                          <motion.h3
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.05 }}
                            className="text-lg sm:text-2xl md:text-3xl font-lora font-normal text-primaryDark leading-tight line-clamp-none sm:line-clamp-2"
                          >
                            {renderTextWithBreaks(card.title)}
                          </motion.h3>

                          {card.subtitle && (
                            <motion.p
                              initial={{ opacity: 0, y: 4 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: 0.1 }}
                              className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-third font-sans pt-0.5 line-clamp-none sm:line-clamp-2"
                            >
                              {renderTextWithBreaks(card.subtitle)}
                            </motion.p>
                          )}

                          {card.description && (
                            <motion.p
                              initial={{ opacity: 0, y: 4 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: 0.15 }}
                              className="text-xs sm:text-sm font-light font-sans text-primaryDark/85 leading-relaxed line-clamp-none sm:line-clamp-4 pt-0.5"
                            >
                              {renderTextWithBreaks(card.description)}
                            </motion.p>
                          )}

                          {card.subdescription && (
                            <motion.div
                              initial={{ opacity: 0, y: 4 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: 0.2 }}
                              className="border-l-2 border-primary/60 pl-3 py-1 my-1.5 text-xs sm:text-sm font-lora italic font-medium text-primaryDark/95 leading-relaxed line-clamp-none sm:line-clamp-4 bg-primary/5 rounded-r-sm"
                            >
                              {renderTextWithBreaks(card.subdescription)}
                            </motion.div>
                          )}
                        </div>

                        {/* STATS ARRAY */}
                        {card.stats && card.stats.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.25 }}
                            className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 mt-auto border-t border-primaryDark/15 flex-shrink-0"
                          >
                            {card.stats.map((stat, sIdx) => {
                              const numStr = String(stat.number || "");
                              const hasPlus = numStr.includes("+");
                              return (
                                <div
                                  key={stat.id || sIdx}
                                  className="space-y-0.5"
                                >
                                  <div className="flex items-baseline gap-0.5 text-primaryDark font-lora font-bold text-lg sm:text-xl md:text-2xl leading-none">
                                    <span>{numStr}</span>
                                    {!hasPlus && (
                                      <span className="text-third font-semibold text-xs sm:text-sm select-none">
                                        +
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[10px] sm:text-xs font-sans text-primaryDark/75 uppercase tracking-wide leading-tight line-clamp-none sm:line-clamp-2">
                                    {stat.label}
                                  </p>
                                </div>
                              );
                            })}
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
